import { formControlClasses, inputClasses } from '@mui/material';
import { Stack } from '@mui/system';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  Flow,
  FlowStop,
  HighwayArea,
  ManualArea,
  RackingArea,
  SingularStationArea,
  StationaryArea,
} from '@thive/fddm-types';
import { useTranslation } from 'react-i18next';

import { SelectInput } from '@/modules/common/components/inputs/components/SelectInput';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { Area } from '@/modules/floorplanDesign/types';
import { getVehicleTypesByArea } from '@/modules/floorplanDesign/helpers/vehicleType';
import { areaFactory } from '@/modules/floorplanDesign/factories';
import { assertNever } from '@/helpers/utils';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { getCommonString } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { usePropertyPanelConfirm } from '@/modules/common/components/usePropertyPanelConfirm';

type VehicleTypeOption = {
  label: string;
  value: string;
};

interface Props {
  selectedAreas: Area[];
}

const containerSx = {
  [`& .${formControlClasses.root} .${inputClasses.root}`]: {
    height: 'auto',
    padding: '4px 12px!important',
  },
};

const getSelectedVehicleTypeId = (areas: Area[]): string | null => {
  const values = areas.map((area) => getVehicleTypesByArea(area)[0] ?? '');
  return getCommonString(values);
};

const withSingularVehicleType = (
  area: SingularStationArea,
  vehicleTypeId: string,
): SingularStationArea => ({
  ...area,
  loadHandlingPoiSpecs: [
    areaFactory.buildLoadHandlingPoiSpecBase(area.name, area.loadHandlingType, vehicleTypeId),
  ],
});

const withStationaryVehicleType = (
  area: StationaryArea,
  vehicleTypeId: string,
): StationaryArea => ({
  ...area,
  stationaryPoiSpecs: [areaFactory.buildStationaryPoiSpec(area.id, vehicleTypeId)],
});

const withRackingVehicleType = (area: RackingArea, vehicleTypeId: string): RackingArea => ({
  ...area,
  vehicleTypeId,
});

const withHighwayVehicleType = (area: HighwayArea, vehicleTypeId: string): HighwayArea => ({
  ...area,
  highwayPoiSpecs: [areaFactory.buildHighwayAreaPoiSpec(area.id, vehicleTypeId)],
});

const withManualVehicleType = (area: ManualArea, vehicleTypeId: string): ManualArea => ({
  ...area,
  vehicleTypeIds: [vehicleTypeId],
});

const withAreaVehicleType = (area: Area, vehicleTypeId: string): Area => {
  switch (area.areaType) {
    case 'SINGULAR':
      return withSingularVehicleType(area, vehicleTypeId);

    case 'STATIONARY':
      return withStationaryVehicleType(area, vehicleTypeId);

    case 'RACKING':
      return withRackingVehicleType(area, vehicleTypeId);

    case 'HIGHWAY':
      return withHighwayVehicleType(area, vehicleTypeId);

    case 'MANUAL':
      return withManualVehicleType(area, vehicleTypeId);

    default:
      return assertNever(area);
  }
};

/**
 * @description All areas in a flow stop must share one vehicle type. Returns the flow stops the
 * changed areas have to be removed from: a stop is left alone when all of its areas end up with
 * the new vehicle type.
 */
const getFlowStopsWithoutChangedAreas = (
  relatedFlowStops: FlowStop[],
  getAreaById: (id: string) => Area | undefined,
  changedAreaIds: ReadonlySet<string>,
  vehicleTypeId: string,
): FlowStop[] => {
  const getNextVehicleTypeId = (areaId: string): string | undefined => {
    if (changedAreaIds.has(areaId)) return vehicleTypeId;
    const area = getAreaById(areaId);
    return area ? getVehicleTypesByArea(area)[0] : undefined;
  };

  return relatedFlowStops.flatMap((flowStop): FlowStop[] => {
    if (flowStop.areaIds.every((areaId) => getNextVehicleTypeId(areaId) === vehicleTypeId)) {
      return [];
    }

    const remainingAreaIds = flowStop.areaIds.filter((areaId) => !changedAreaIds.has(areaId));
    return isAtLeast1(remainingAreaIds) ? [{ ...flowStop, areaIds: remainingAreaIds }] : [];
  });
};

/**
 * @description Flows need the same vehicle type on both ends, so flows on areas or flow stops whose
 * vehicle type changes are deleted. Flow stops that only lose the changed areas keep their flows.
 */
const getFlowsToDelete = (
  flows: Flow[],
  changedAreaIds: ReadonlySet<string>,
  relatedFlowStops: FlowStop[],
  flowStopsToUpdate: FlowStop[],
): Flow[] => {
  const updatedFlowStopIds = new Set(flowStopsToUpdate.map(({ id }) => id));
  const changedEndpointIds = new Set([
    ...changedAreaIds,
    // Flow stops that aren't updated end up fully on the new vehicle type
    ...relatedFlowStops.filter(({ id }) => !updatedFlowStopIds.has(id)).map(({ id }) => id),
  ]);

  return flows.filter(
    ({ sourceAreaOrFlowStopId, destinationAreaOrFlowStopId }) =>
      changedEndpointIds.has(sourceAreaOrFlowStopId) ||
      changedEndpointIds.has(destinationAreaOrFlowStopId),
  );
};

function VehicleTypeComponent({ selectedAreas }: Props) {
  const mode = useAppStore((state) => state.mode);
  const allVehicles = useVehicleStore((state) => state.allVehicles);
  const enabledVehicleTypeIds = useFloorplanDesignStore(
    useShallow((state) => state.getVehicleTypeIds()),
  );
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);
  const { confirm, Dialog } = usePropertyPanelConfirm();
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const options = useMemo<VehicleTypeOption[]>(
    () =>
      allVehicles.getByIds(enabledVehicleTypeIds).map((vehicle) => ({
        label: vehicle.name,
        value: vehicle.id,
      })),
    [allVehicles, enabledVehicleTypeIds],
  );

  const selectedVehicleTypeId = getSelectedVehicleTypeId(selectedAreas);
  const isDisabled = mode !== 'EDITABLE';

  const handleChange = useCallback(
    async (vehicleTypeId: string) => {
      if (isDisabled || selectedAreas.length === 0) return;
      if (!vehicleTypeId || vehicleTypeId === selectedVehicleTypeId) return;

      const updatedAreas = createEmptyAreas();
      selectedAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, withAreaVehicleType(area, vehicleTypeId));
      });

      const changedAreaIds = new Set(
        selectedAreas
          .filter((area) => getVehicleTypesByArea(area)[0] !== vehicleTypeId)
          .map(({ id }) => id),
      );
      const { getRelatedFlowStops, getAreaById, getFlows } = useFloorplanDesignStore.getState();
      const relatedFlowStops = getRelatedFlowStops([...changedAreaIds]);
      const flowStopsToUpdate = getFlowStopsWithoutChangedAreas(
        relatedFlowStops,
        getAreaById,
        changedAreaIds,
        vehicleTypeId,
      );
      const flowsToDelete = getFlowsToDelete(
        getFlows(),
        changedAreaIds,
        relatedFlowStops,
        flowStopsToUpdate,
      );

      // One confirm listing everything the change affects
      if (flowsToDelete.length > 0 || flowStopsToUpdate.length > 0) {
        const messageParts = [
          flowsToDelete.length > 0 &&
            t('interface:properties.flows.change_vehicle_type_confirm.flows', {
              count: flowsToDelete.length,
            }),
          flowStopsToUpdate.length > 0 &&
            t('interface:properties.flows.change_vehicle_type_confirm.flow_stops', {
              count: flowStopsToUpdate.length,
            }),
          t('interface:properties.flows.change_vehicle_type_confirm.continue'),
        ];
        const confirmed = await confirm(
          t('interface:properties.flows.change_vehicle_type_confirm.label'),
          messageParts.filter(Boolean).join(' '),
        );
        if (!confirmed) return;
      }

      // One undo step for the vehicle type change and the flows and flow stops it affects
      historySvc.batch(() => {
        fddmSvc.setPreference('area.vehicleTypeId', vehicleTypeId);
        if (flowsToDelete.length > 0) fddmSvc.deleteFlows(flowsToDelete);
        fddmSvc.updateAreas(updatedAreas);
        flowStopsToUpdate.forEach((flowStop) => fddmSvc.updateFlowStop(flowStop));
      });
    },
    [confirm, fddmSvc, historySvc, isDisabled, selectedAreas, selectedVehicleTypeId, t],
  );

  return (
    <>
      <Dialog anchor={ref} offset={[0, 100]} />
      <div ref={ref}>
        <PropertiesLabel i18nkey='interface:properties.area.supported_vehicle.label'>
          <Stack sx={containerSx}>
            <SelectInput
              ellipsis
              disabled={isDisabled}
              options={options}
              onChange={handleChange}
              value={selectedVehicleTypeId}
              showMixed
            />
          </Stack>
        </PropertiesLabel>
      </div>
    </>
  );
}

export const VehicleType = memo(VehicleTypeComponent);
